import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog';
import { Card } from '@/components/dashboard/DashboardCard';
import { cn } from '@/lib/cn';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';

interface DialogWrapperProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  children: ReactNode;
  className?: string;
  cardClassName?: string;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '3xl' | '4xl' | '5xl' | 'full';
  showCloseButton?: boolean;
}

const maxWidthMap = {
  sm: 'md:max-w-sm',
  md: 'md:max-w-md',
  lg: 'md:max-w-lg',
  xl: 'md:max-w-xl',
  '3xl': 'md:max-w-3xl',
  '4xl': 'md:max-w-4xl',
  '5xl': 'md:max-w-5xl',
  full: 'md:max-w-[calc(100%-2rem)]',
};

export function DialogWrapper({
  open,
  onOpenChange,
  title,
  children,
  className,
  cardClassName,
  maxWidth = '3xl',
  showCloseButton = true,
}: DialogWrapperProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        variant="default"
        aria-describedby={undefined}
        className={cn('p-6', className)}
      >
        <Card
          className={cn(
            `flex h-auto max-h-full w-full flex-col gap-0 overflow-y-auto p-6 shadow-none md:h-auto md:max-h-[70vh] md:min-w-150 lg:min-w-200`,
            maxWidthMap[maxWidth],
            cardClassName,
          )}
        >
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="text-secondary pb-2 text-2xl font-light">
              {title}
            </DialogTitle>
            {showCloseButton && (
              <DialogClose className="text-muted-foreground hover:text-foreground md:block">
                <X className="size-6" />
                <span className="sr-only">Close</span>
              </DialogClose>
            )}
          </DialogHeader>

          <div>{children}</div>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
